import { useState } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { writeLog } from '../../utils/firebaseUtils';
import { actorStamp } from '../../utils/actorStamp';

const S = {
  bg: '#0c112e', panel: '#141936', border: '#2e3351',
  text: '#dee0ff', dim: '#64748b', accent: '#89ceff',
  green: '#10b981', red: '#f43f5e',
  mono: 'monospace', sans: "'Inter', system-ui, sans-serif",
};

/**
 * Completion-evidence modal — required whenever a task has requiresProof
 * (default true). Requires a text note or at least one attachment before the
 * task can be marked completed. Writes completion fields onto the task doc,
 * and — for tasks auto-created from a Mission Log @-tag (sourceType ===
 * 'mission_log') — syncs the matching daily_updates/{sourceId}/taggedTasks/{uid}
 * doc so the mission log post reflects the same completion.
 */
export default function TaskCompleteModal({ task, userId, userName, onClose, onCompleted }) {
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState([]); // [{name,url,size}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = !!note.trim() || attachments.length > 0;

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tasks/completions/${task.id}/${Date.now()}_${safeName}`;
      const uploadTask = uploadBytesResumable(ref(storage, path), file);
      await new Promise((res, rej) => uploadTask.on('state_changed', null, rej, res));
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      setAttachments(prev => [...prev, { name: file.name, url, size: file.size }]);
    } catch (ex) {
      setError('Upload failed: ' + (ex.message || 'Unknown error'));
    }
    setUploading(false);
    e.target.value = '';
  }

  async function submit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    const db = getFirestore(getApp());
    try {
      await updateDoc(doc(db, 'tasks', task.id), { ...actorStamp(),
        status: 'completed',
        completionNote: note.trim(),
        completionAttachments: attachments,
        completedByUid: userId,
        completedByName: userName,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (task.sourceType === 'mission_log' && task.sourceId) {
        try {
          await updateDoc(doc(db, 'daily_updates', task.sourceId, 'taggedTasks', userId), {
            status: 'completed',
            completedAt: serverTimestamp(),
            completedByName: userName,
          });
        } catch (_) { /* best-effort — task completion itself already succeeded */ }
      }

      writeLog('task_completed', {
        taskId: task.id,
        title: task.title,
        requiresProof: task.requiresProof !== false,
        hadAttachment: attachments.length > 0,
      }).catch(() => {});

      onCompleted?.();
      onClose?.();
    } catch (ex) {
      setError(ex.message || 'Could not mark complete');
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ width: '100%', maxWidth: 480, background: S.panel, border: `1px solid ${S.border}`,
        borderRadius: 10, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${S.border}`,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: S.mono, fontSize: 9, color: S.green, letterSpacing: 2 }}>COMPLETE TASK</span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontFamily: S.sans, fontSize: 13, fontWeight: 700, color: S.text, marginBottom: 4 }}>{task.title}</div>
          <p style={{ fontFamily: S.sans, fontSize: 11, color: S.dim, margin: '0 0 12px', lineHeight: 1.5 }}>
            Add a note or attach a file to close this out — required before it can be marked complete.
          </p>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="What did you do? (or attach a file below)"
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box', background: S.bg, border: `1px solid ${S.border}`,
              color: S.text, fontFamily: S.sans, fontSize: 13, lineHeight: 1.6, padding: '10px 12px',
              resize: 'vertical', marginBottom: 10, borderRadius: 6 }} />

          {attachments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {attachments.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: S.mono, fontSize: 10, color: S.accent }}>
                  <span>📎 {a.name}</span>
                  <button onClick={() => setAttachments(p => p.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: S.red, cursor: 'pointer', fontSize: 12 }}>×</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontFamily: S.mono, fontSize: 8, padding: '6px 12px', cursor: 'pointer',
              background: 'transparent', border: `1px solid ${S.border}`, color: S.dim, borderRadius: 4 }}>
              {uploading ? 'UPLOADING…' : '📎 ATTACH FILE'}
              <input type="file" hidden onChange={handleFile} disabled={uploading} />
            </label>
            <div style={{ flex: 1 }} />
            <button onClick={onClose}
              style={{ fontFamily: S.mono, fontSize: 8, padding: '7px 14px', cursor: 'pointer',
                background: 'transparent', border: `1px solid ${S.border}`, color: S.dim, borderRadius: 4 }}>
              CANCEL
            </button>
            <button onClick={submit} disabled={!canSubmit || submitting}
              style={{ fontFamily: S.mono, fontSize: 8, padding: '7px 16px', cursor: canSubmit ? 'pointer' : 'default',
                background: canSubmit ? `${S.green}25` : 'transparent',
                border: `1px solid ${canSubmit ? S.green : S.border}`,
                color: canSubmit ? S.green : S.dim, fontWeight: 700, borderRadius: 4 }}>
              {submitting ? 'SAVING…' : 'MARK COMPLETE'}
            </button>
          </div>
          {error && <div style={{ fontFamily: S.mono, fontSize: 9, color: S.red, marginTop: 10 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
